import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import {
	COMPANY_FIELDS_MAX_LENGTH,
	DESCRIPTION_MAX_LENGTH,
	TITLE_MAX_LENGTH,
	URL_MAX_LENGTH,
} from 'constants/validationConstants'
import { RhcField } from 'features/generator/components/RhcField'
import { useAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'
import { maxLength } from 'utils/inputChangeUtils'

import { transformBackendResponseKey } from '@/config'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { getDocumentInfoSchema } from './pdfFormModel'

export const DOCUMENT_INFO_FORM_ID = 'documentInfo'

export const documentCompanyNameAtom = atomWithReset('')
export const documentFileDescriptionAtom = atomWithReset('')
export const documentTitleAtom = atomWithReset('')
export const documentWebsiteAtom = atomWithReset('')

export const DocumentInfo = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const [companyName, setCompanyName] = useAtom(documentCompanyNameAtom)
	const [title, setTitle] = useAtom(documentTitleAtom)
	const [website, setWebsite] = useAtom(documentWebsiteAtom)
	const [description, setDescription] = useAtom(documentFileDescriptionAtom)

	const form = useForm({
		defaultValues: {
			company: {
				name: getStore().get(documentCompanyNameAtom),
				title: getStore().get(documentTitleAtom),
				website: getStore().get(documentWebsiteAtom),
				description: getStore().get(documentFileDescriptionAtom),
			},
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(getDocumentInfoSchema(t)),
		shouldUnregister: false,
	})

	const { handleSubmit } = form

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm((onValid, onInvalid) =>
					handleSubmit(onValid, async (errors) => {
						onInvalid(errors)
					}),
				),
		}),
		[handleSubmit],
	)

	return (
		<div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 tablet:gap-x-12 tablet:gap-y-4">
			<RhcField
				type="text"
				form={form}
				path="company.name"
				value={companyName}
				setValue={(value) => setCompanyName(value || '')}
				fieldName="company_name"
				onChangeTransform={(event) =>
					maxLength(COMPANY_FIELDS_MAX_LENGTH + 1)(event)
				}
				className="w-full"
				label={t(
					'generator__content_form_section__pdf__company_name_label',
				)}
				placeholder={t(
					'generator__content_form_section__pdf__company_name_placeholder',
				)}
			/>
			<RhcField
				type="text"
				form={form}
				path="company.title"
				value={title}
				setValue={(value) => setTitle(value || '')}
				fieldName="title"
				onChangeTransform={(event) =>
					maxLength(TITLE_MAX_LENGTH + 1)(event)
				}
				className="w-full"
				label={t('generator__content_form_section__pdf__title_label')}
				placeholder={t(
					'generator__content_form_section__pdf__title_placeholder',
				)}
			/>
			<RhcField
				type="text"
				form={form}
				path="company.description"
				value={description}
				setValue={(value) => setDescription(value || '')}
				fieldName="description"
				onChangeTransform={(event) =>
					maxLength(DESCRIPTION_MAX_LENGTH + 1)(event)
				}
				className="w-full"
				label={t(
					'generator__content_form_section__pdf__file_description_label',
				)}
				placeholder={t(
					'generator__content_form_section__pdf__file_description_placeholder',
				)}
			/>
			<RhcField
				type="url"
				form={form}
				path="company.website"
				value={website}
				setValue={(value) => setWebsite(value || '')}
				fieldName="website"
				onChangeTransform={(event) =>
					maxLength(URL_MAX_LENGTH + 1)(event)
				}
				className="w-full"
				label={t('generator__content_form_section__pdf__website_label')}
				placeholder={t(
					'generator__content_form_section__pdf__website_placeholder',
				)}
			/>
		</div>
	)
})

DocumentInfo.displayName = 'DocumentInfo'

export const documentInfoFormConfig = (
	t: TTranslationFunction,
	disabled: boolean,
): TQrContentFormSection => {
	return {
		id: DOCUMENT_INFO_FORM_ID,
		title: t('generator__content_form_section__pdf__document_title'),
		description: t(
			'generator__content_form_section__pdf__document_description',
		),
		icon: <LineInfoCircle className="text-info-color fill-info-color" />,
		Content: DocumentInfo,
		disabled: disabled,
	}
}
